import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactToPrint from 'react-to-print';

import { useRecoilValue } from 'recoil';

import { getTransactionActivities, sendReceiptByEmail } from 'api/Transaction';

import { useReceipt } from 'lib/hooks/queries/billing/useReceipt';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import { currentPractice } from 'components/practice/practiceState';
import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Modal from 'components/shared/Modal/Modal';
import Receipt from 'components/shared/Receipt/Receipt';
import { userState } from 'components/state';

import ExpandableToolbar from '../ExpandableToolbar/ExpandableToolbar';
import Loader from '../Loader/Loader';

const ReceiptModal = ({
  isOpen,
  onClose,
  transactionId,
  appointmentId,
  viewDefaultReceipt = true,
  walkout = false,
  isExternal = false
}) => {
  const { isOpen: isReceiptOpen, closeModal: closeReceiptModal } = useModal();
  const receiptRef = useRef();
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);
  const user = useRecoilValue(userState);

  const isPatient = !user.logged_in || (user?.logged_in && !practice?.id);

  const { data, isLoading } = useReceipt({
    params: { transactionId, appointmentId, isExternal },
    dependencies: [transactionId, appointmentId],
    options: { enabled: (!!transactionId || !!appointmentId) && viewDefaultReceipt }
  });

  const { mutateAsync: sendReceiptByEmailHandler, isLoading: isSendingReceiptEmail } =
    useCustomMutation({
      mutationFn: () => sendReceiptByEmail(navigate, { transactionId }),
      successMessage: 'Receipt Email Sent!',
      errorMessage: 'There was an error sending the receipt email. Please try again.'
    });

  const receipt = data?.receipt;

  const handleCloseReceipt = () => {
    closeReceiptModal();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Receipt"
      handleOpen={isReceiptOpen}
      handleClose={handleCloseReceipt}
      handleClick={handleCloseReceipt}
      bodyClassName="p-0"
      width="full"
      headButton={
        <div className="flex w-full items-center justify-end gap-2 pr-3">
          <Button
            variant="link"
            size="small"
            text="Email"
            icon="mail-mono"
            color="neutral"
            iconSize={16}
            shade={700}
            iconShade={700}
            iconColor="neutral"
            className="gap-1.5"
            data-qa="email-to-patient"
            onClick={sendReceiptByEmailHandler}
            isLoading={isSendingReceiptEmail}
          />
          <ReactToPrint
            trigger={() => (
              <Button
                size="small"
                variant="link"
                color="neutral"
                iconSize={16}
                shade={700}
                iconShade={700}
                iconColor="neutral"
                text="Print"
                icon="printer-mono"
              />
            )}
            content={() => receiptRef.current}
          />
        </div>
      }
      isFooter={false}>
      <div className="flex h-full w-full">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader color="primary" size={40} />
          </div>
        ) : (
          receipt && <Receipt receipt={receipt} ref={receiptRef} walkout={walkout} />
        )}
        {!isLoading && !isPatient && (
          <ExpandableToolbar
            category="transaction"
            id={receipt?.transaction?.id}
            fetchData={getTransactionActivities}
          />
        )}
      </div>
    </Modal>
  );
};

export default withErrorBoundary(ReceiptModal);
